import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Calendar, Building2, Briefcase, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "wouter";

export default function BusinessSolutions() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-indigo-950 text-white py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-serif font-medium mb-6 leading-tight tracking-tight">
              Longevity Practice Solutions
            </h1>
            <div className="h-px bg-indigo-300/30 w-16 mx-auto mb-8"></div>
            <p className="text-sm text-indigo-100 max-w-2xl mx-auto mb-8 leading-relaxed">
              Strategic consulting to help organizations build and scale profitable longevity medicine services. Serving investors, health systems, and clinics focused on next-generation medicine.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/consulting">
                <Button className="bg-white text-indigo-950 hover:bg-gray-100 text-xs px-6 py-2 rounded-sm h-auto">
                  Explore Consulting Services
                </Button>
              </Link>
              
              <a href="https://calendar.notion.so/meet/anant-wje3h1hoi/sn2go4gd4" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 text-xs px-6 py-2 rounded-sm h-auto">
                  Book a Strategy Call
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Services Overview */}
      <div className="py-16 container mx-auto px-4 max-w-5xl">
        <div className="mb-10">
          <h2 className="text-2xl font-serif font-medium mb-2 text-gray-900">
            Strategic Solutions
          </h2>
          <div className="h-px bg-indigo-900/30 w-16 mb-4"></div>
          <p className="text-sm text-gray-700 max-w-2xl mb-12">
            From startup clinics to established enterprises, we provide tailored solutions to help you succeed in the competitive longevity medicine landscape.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Service 1 */}
          <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mb-4">01</p>
            <h3 className="text-base font-medium mb-3 text-gray-900">Practice Setup & Design</h3>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              Strategic guidance on establishing and scaling a successful longevity clinic from the ground up.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Business model selection</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Operational framework implementation</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Technology stack selection</span>
              </li>
            </ul>
          </div>
          
          {/* Service 2 */}
          <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mb-4">02</p>
            <h3 className="text-base font-medium mb-3 text-gray-900">Clinical Protocol Development</h3>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              Evidence-based protocols customized for your patient population and practice focus.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Peptide & hormone protocols</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Biomarker assessment frameworks</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Treatment pathway documentation</span>
              </li>
            </ul>
          </div>
          
          {/* Service 3 */}
          <div className="border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <p className="text-[11px] uppercase tracking-wider text-indigo-900 font-medium mb-4">03</p>
            <h3 className="text-base font-medium mb-3 text-gray-900">Technology Implementation</h3>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              Strategic guidance on selecting and optimizing technology for your longevity practice.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— EHR platform selection</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Telehealth implementation</span>
              </li>
              <li className="flex items-start">
                <span className="text-sm text-gray-700">— Digital health tool integration</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-indigo-950 py-16 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-serif font-medium mb-2">
              Ready to Transform Your Practice?
            </h2>
            <div className="h-px bg-indigo-300/30 w-16 mb-6"></div>
            <p className="text-sm text-indigo-100 mb-8 leading-relaxed">
              My firm has consulted with early-stage startups through publicly-traded companies in healthcare. Book a strategy call to discuss how we can help you succeed in longevity medicine.
            </p>
            <a href="https://calendar.notion.so/meet/anant-wje3h1hoi/sn2go4gd4" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-indigo-950 hover:bg-gray-100 text-xs px-6 py-2 rounded-sm h-auto">
                Schedule Your Strategy Call
              </Button>
            </a>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}